
const MAX_SPAN_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April',
  'Mai', 'Juni', 'Juli', 'August',
  'September', 'Oktober', 'November', 'Dezember',
];

function parseSwissDate(value) {
  const match = String(value ?? '').match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCDate() !== Number(day) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCFullYear() !== Number(year)
  ) {
    return null;
  }

  return date;
}

function formatSwissDate(date) {
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}.${month}.${year}`;
}

function daysInclusive(start, end) {
  return Math.round((end - start) / DAY_MS) + 1;
}

// entries: array of { kwh, chf, periodStart, periodEnd } for successfully
// parsed invoices only.
export function aggregateElectricityInvoiceEntries(entries, reportingYear) {
  const year = Number(reportingYear);
  const warnings = [];
  const missingMonths = [];
  const partialMonths = [];
  const overlaps = [];
  const allocations = [];
  const skippedEntries = [];
  let hasBlockingIssues = false;
  let hasEstimatedValues = false;

  if (
    reportingYear == null ||
    reportingYear === '' ||
    !Number.isInteger(year) ||
    year < 2000 ||
    year > 9999
  ) {
    return {
      totalKwh: null,
      kwhCount: 0,
      totalChf: null,
      chfCount: 0,
      periodStart: null,
      periodEnd: null,
      spanDays: null,
      periodWarning: 'Kein gültiges Berichtsjahr vorhanden.',
      missingMonths,
      partialMonths,
      overlaps,
      allocations,
      skippedEntries,
      warnings: ['Kein gültiges Berichtsjahr vorhanden.'],
      hasBlockingIssues: true,
      hasEstimatedValues,
    };
  }

  const yearStart = new Date(Date.UTC(year, 0, 1));
  const yearEnd = new Date(Date.UTC(year, 11, 31));

  // Rechnungsperioden prüfen und auf das Berichtsjahr begrenzen.
  const validEntries = [];

  entries.forEach((entry, index) => {
    const start = parseSwissDate(entry.periodStart);
    const end = parseSwissDate(entry.periodEnd);
    const fileName = entry.fileName || `Rechnung ${index + 1}`;

    if (!start || !end || start > end) {
      skippedEntries.push({
        id: entry.id,
        fileName,
        reason: 'invalid-period',
      });

      warnings.push(
        `${fileName}: Die Abrechnungsperiode fehlt oder ist ungültig.`
      );

      hasBlockingIssues = true;
      return;
    }

    const relevantStart = new Date(
      Math.max(start.getTime(), yearStart.getTime())
    );
    const relevantEnd = new Date(
      Math.min(end.getTime(), yearEnd.getTime())
    );

    if (relevantStart > relevantEnd) {
      skippedEntries.push({
        id: entry.id,
        fileName,
        reason: 'outside-reporting-year',
      });
      return;
    }

    validEntries.push({
      ...entry,
      fileName,
      start,
      end,
      relevantStart,
      relevantEnd,
      overlapping: false,
    });
  });

  if (
    entries.length > 0 &&
    validEntries.length === 0 &&
    skippedEntries.some(entry => entry.reason === 'outside-reporting-year')
  ) {
    warnings.push(
      `Keine der hochgeladenen Rechnungen betrifft das Berichtsjahr ${year}.`
    );

    hasBlockingIssues = true;
  }

  // Überlappende Rechnungsperioden erkennen.
  for (let i = 0; i < validEntries.length; i++) {
    for (let j = i + 1; j < validEntries.length; j++) {
      const first = validEntries[i];
      const second = validEntries[j];

      const overlapStart = new Date(
        Math.max(
          first.relevantStart.getTime(),
          second.relevantStart.getTime()
        )
      );
      const overlapEnd = new Date(
        Math.min(
          first.relevantEnd.getTime(),
          second.relevantEnd.getTime()
        )
      );

      if (overlapStart <= overlapEnd) {
        first.overlapping = true;
        second.overlapping = true;

        overlaps.push({
          firstFile: first.fileName,
          secondFile: second.fileName,
          start: formatSwissDate(overlapStart),
          end: formatSwissDate(overlapEnd),
        });
      }
    }
  }

  if (overlaps.length > 0) {
    hasBlockingIssues = true;

    warnings.push(
      'Überlappende Rechnungsperioden erkannt. Die betroffenen ' +
      'Rechnungen wurden nicht automatisch summiert.'
    );
  }

  // Jahresanteile nur aus konfliktfreien Rechnungen berechnen.
  const eligibleEntries = validEntries
    .filter(entry => {
      if (entry.overlapping) {
        skippedEntries.push({
          id: entry.id,
          fileName: entry.fileName,
          reason: 'overlap',
        });

        return false;
      }

      return true;
    })
    .map(entry => {
      const totalDays = daysInclusive(entry.start, entry.end);
      const relevantDays = daysInclusive(
        entry.relevantStart,
        entry.relevantEnd
      );
      const factor = relevantDays / totalDays;

      const kwh =
        typeof entry.kwh === 'number' &&
          Number.isFinite(entry.kwh) &&
          entry.kwh >= 0
          ? entry.kwh * factor
          : null;

      const chf =
        typeof entry.chf === 'number' &&
          Number.isFinite(entry.chf) &&
          entry.chf >= 0
          ? entry.chf * factor
          : null;

      if (factor < 1 && (kwh != null || chf != null)) {
        hasEstimatedValues = true;
      }

      allocations.push({
        id: entry.id,
        fileName: entry.fileName,
        periodStart: formatSwissDate(entry.relevantStart),
        periodEnd: formatSwissDate(entry.relevantEnd),
        factor,
        kwh,
        chf,
        estimated: factor < 1,
      });

      return {
        ...entry,
        kwh,
        chf,
      };
    });

  const totalKwh = eligibleEntries.reduce(
    (sum, entry) => (entry.kwh != null ? sum + entry.kwh : sum),
    0
  );
  const kwhCount = eligibleEntries.filter(entry => entry.kwh != null).length;

  const totalChf = eligibleEntries.reduce(
    (sum, entry) => (entry.chf != null ? sum + entry.chf : sum),
    0
  );
  const chfCount = eligibleEntries.filter(entry => entry.chf != null).length;

  const starts = validEntries
    .map(entry => entry.start)
    .filter(Boolean);
  const ends = validEntries
    .map(entry => entry.end)
    .filter(Boolean);

  let periodStart = null;
  let periodEnd = null;
  let spanDays = null;
  let periodWarning = null;

  if (starts.length > 0 && ends.length > 0) {
    const minStart = new Date(Math.min(...starts));
    const maxEnd = new Date(Math.max(...ends));

    periodStart = formatSwissDate(minStart);
    periodEnd = formatSwissDate(maxEnd);
    spanDays = Math.round((maxEnd - minStart) / DAY_MS);

    if (spanDays > MAX_SPAN_DAYS) {
      periodWarning = `Die hochgeladenen Rechnungen decken zusammen einen Zeitraum von ${spanDays} Tagen ab (${periodStart} – ${periodEnd}) — das ist mehr als ein Jahr. Es werden nur Werte des Berichtsjahres ${year} berücksichtigt.`;
      warnings.push(periodWarning);
    }
  }

  // Fehlende und teilweise abgedeckte Monate ermitteln.
  // Rechnungen mit Überlappungen gelten bis zur Klärung
  // nicht als vollständig erfasste Daten.
  if (eligibleEntries.length > 0) {
    const coveredDays = new Set();

    eligibleEntries.forEach(entry => {
      for (
        let day = entry.relevantStart.getTime();
        day <= entry.relevantEnd.getTime();
        day += DAY_MS
      ) {
        coveredDays.add(day);
      }
    });

    for (let month = 0; month < 12; month++) {
      const monthStart = new Date(Date.UTC(year, month, 1));
      const monthEnd = new Date(Date.UTC(year, month + 1, 0));
      const monthDays = daysInclusive(monthStart, monthEnd);

      let covered = 0;

      for (
        let day = monthStart.getTime();
        day <= monthEnd.getTime();
        day += DAY_MS
      ) {
        if (coveredDays.has(day)) {
          covered++;
        }
      }

      if (covered === 0) {
        missingMonths.push(MONTH_NAMES[month]);
      } else if (covered < monthDays) {
        partialMonths.push({
          month: MONTH_NAMES[month],
          coveredDays: covered,
          totalDays: monthDays,
        });
      }
    }

    if (missingMonths.length > 0) {
      warnings.push(
        `Fehlende Monate ${year}: ${missingMonths.join(', ')}.`
      );
    }

    if (partialMonths.length > 0) {
      warnings.push(
        'Folgende Monate sind nur teilweise abgedeckt: ' +
        partialMonths
          .map(item =>
            `${item.month} (${item.coveredDays}/${item.totalDays} Tage)`
          )
          .join(', ') + '.'
      );
    }
  }

  if (hasEstimatedValues) {
    warnings.push(
      'Jahresübergreifende Rechnungen wurden zeitanteilig ' +
      'auf das Berichtsjahr aufgeteilt. Diese Werte sind ' +
      'Schätzungen und müssen überprüft werden.'
    );
  }

  if (!periodWarning && warnings.length > 0) {
    periodWarning = warnings.join(' ');
  }

  return {
    totalKwh: kwhCount > 0 ? totalKwh : null,
    kwhCount,
    totalChf: chfCount > 0 ? totalChf : null,
    chfCount,
    periodStart,
    periodEnd,
    spanDays,
    periodWarning,
    missingMonths,
    partialMonths,
    overlaps,
    allocations,
    skippedEntries,
    warnings,
    hasBlockingIssues,
    hasEstimatedValues,
  };
}